import React, { type ReactNode, useCallback } from 'react';
import { CLAUSE_TYPE_TERMINAL } from '@atlaskit/jql-ast';
import type { FieldOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import {
	ContextualAnalyticsData,
	INLINE_DIALOG,
	useAnalyticsEvents,
	fireUIAnalytics,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	hydrateJqlBuilderQuery$data,
	JiraJqlViewContext,
	JiraJqlScopeInput,
} from '@atlassian/jira-relay/src/__generated__/hydrateJqlBuilderQuery.graphql';
import type { ui_jqlBuilder_JQLBuilderUI_hydrationQueryData$data } from '@atlassian/jira-relay/src/__generated__/ui_jqlBuilder_JQLBuilderUI_hydrationQueryData.graphql.ts';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import {
	PickerTypes,
	type FieldToRender,
	type OnClearPicker,
	type OnSearchWrapper,
	type OnSwitchToJql,
} from '../../../common/types.tsx';
import ListItemChild from '../../../common/ui/list-item-child/index.tsx';
import { StyledChild, StyledListItemChild } from '../../../common/ui/styled.tsx';
import type { JiraProjectOptionsType } from '../../../common/ui/types.tsx';
import { useActivePopupKey } from '../../../controllers/active-popup/index.tsx';
import { useSendOptionAnalytics } from '../../../utils/send-option-analytics.tsx';
import { CascadeSelectPicker } from './cascade-select-picker/index.tsx';
import { DatePickerWrapper } from './date-picker-wrapper/index.tsx';
import { LastUserCommentedPickerWrapper } from './last-user-commented-picker-wrapper/index.tsx';
import { NumberRangePicker } from './number-range-picker/index.tsx';
import { PickerWrapper } from './picker-wrapper/main.tsx';
import { TextAreaPicker } from './text-area-picker/main.tsx';
import { UnsupportedPickerWrapper } from './unsupported-picker-wrapper/index.tsx';

type FieldProps = {
	fieldToRender: FieldToRender;
	fieldsToRender: FieldToRender[];
	loading: boolean;
	hydratedData: hydrateJqlBuilderQuery$data | ui_jqlBuilder_JQLBuilderUI_hydrationQueryData$data;
	onSearchWrapper?: OnSearchWrapper;
	onSwitchToJql?: OnSwitchToJql;
	onClearPicker: OnClearPicker;
	selectedFields: FieldOption[];
	projectOptions?: JiraProjectOptionsType;
	singleSelectFields?: string[];
	shouldUseListMarkup?: boolean;
	hideOperatorDropdown?: boolean;
	contextJql: string;
	viewContext: JiraJqlViewContext | null;
	scope: JiraJqlScopeInput | null;
	suggestedUsersCount?: number;
};

const analyticsAttributes = {
	pickerType: 'field',
} as const;

type SpanType = {
	children: ReactNode;
	useListElement: Boolean;
	handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
} & React.HTMLAttributes<HTMLSpanElement>;

type DivType = {
	children: ReactNode;
	useListElement: Boolean;
	handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
} & React.HTMLAttributes<HTMLDivElement>;

const FieldWrapperNew = ({ children, useListElement, handleKeyDown }: SpanType | DivType) => {
	const StyledListItemChildComponent = isVisualRefreshEnabled()
		? ListItemChild
		: StyledListItemChild;

	return useListElement ? (
		<StyledListItemChildComponent data-vc="jql-builder-basic-field-list-item">
			<span role="presentation" onKeyDown={handleKeyDown}>
				{children}
			</span>
		</StyledListItemChildComponent>
	) : (
		<StyledChild onKeyDown={handleKeyDown}>{children}</StyledChild>
	);
};

export const Field = ({
	fieldToRender,
	onSearchWrapper,
	onSwitchToJql,
	fieldsToRender,
	loading,
	hydratedData,
	selectedFields,
	onClearPicker,
	projectOptions,
	singleSelectFields = [],
	shouldUseListMarkup,
	hideOperatorDropdown,
	contextJql,
	viewContext,
	scope,
	suggestedUsersCount,
}: FieldProps) => {
	const { clause, secondaryClause, fieldId, fieldLabel, picker, fieldDescription, isIrremovable } =
		fieldToRender;

	const sendOptionAnalytics = useSendOptionAnalytics();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { pickerType, propType, fieldType } = picker;

	const [activePopupKey, { openPopup, closePopup, closeAllPopups }] = useActivePopupKey();
	const onOpen = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dropdown',
		});
		const attributes: Attributes = {
			actionType: 'open',
		};
		fireUIAnalytics(analyticsEvent, 'refinementBarBasicPicker', attributes);

		return openPopup(fieldId);
	}, [createAnalyticsEvent, fieldId, openPopup]);

	const onClose = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dropdown',
		});
		const attributes: Attributes = {
			actionType: 'close',
		};
		fireUIAnalytics(analyticsEvent, 'refinementBarBasicPicker', attributes);

		return closePopup(fieldId);
	}, [closePopup, fieldId, createAnalyticsEvent]);

	const onClearCallback = useCallback(
		(onClearJqlClause: string | undefined) => {
			const newSelectedFields = selectedFields.filter(
				(selectedField) => selectedField.value.toLowerCase() !== fieldId.toLowerCase(),
			);

			if (onClearJqlClause !== undefined) {
				onClearPicker({ selectedFields: newSelectedFields });
				onSearchWrapper && onSearchWrapper(onClearJqlClause);
			} else {
				onClearPicker({
					selectedFields: newSelectedFields,
					fieldsToRender: fieldsToRender.filter((field) => fieldId.toLowerCase() !== field.fieldId),
				});
			}
		},
		[fieldId, fieldsToRender, onClearPicker, onSearchWrapper, selectedFields],
	);

	const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement | HTMLSpanElement>) => {
		if (event.key === 'Escape') {
			// avoid closing a modal on closing a picker with esc key
			const isOpen = activePopupKey === fieldId;
			isOpen && event.stopPropagation();
			onClose();
		}
	};

	const UnsupportedFieldPicker = (
		<FieldWrapperNew useListElement={Boolean(shouldUseListMarkup)} handleKeyDown={handleOnKeyDown}>
			<UnsupportedPickerWrapper
				activePopupKey={activePopupKey}
				clause={clause}
				fieldDescription={fieldDescription}
				fieldId={fieldId}
				fieldLabel={fieldLabel}
				onClose={onClose}
				onOpen={onOpen}
				onClear={onClearCallback}
				onSwitchToJql={onSwitchToJql}
			/>
		</FieldWrapperNew>
	);

	switch (pickerType) {
		case PickerTypes.Picker: {
			if (clause && clause.clauseType !== CLAUSE_TYPE_TERMINAL) {
				throw new Error(`Picker type ${pickerType} cannot be used with a non terminal clause`);
			}

			return (
				<ContextualAnalyticsData
					sourceName="valueSearch"
					sourceType={INLINE_DIALOG}
					attributes={analyticsAttributes}
				>
					<FieldWrapperNew
						useListElement={Boolean(shouldUseListMarkup)}
						handleKeyDown={handleOnKeyDown}
					>
						<PickerWrapper
							onSearchWrapper={onSearchWrapper}
							fieldId={fieldId}
							fieldLabel={fieldLabel}
							picker={picker}
							terminalClause={clause || undefined}
							activePopupKey={activePopupKey}
							onOpen={onOpen}
							onClose={onClose}
							onSendOptionAnalytics={sendOptionAnalytics}
							loading={loading}
							hydratedData={hydratedData}
							onClear={onClearCallback}
							projectOptions={projectOptions}
							isMulti={!singleSelectFields.includes(fieldId)}
							fieldDescription={fieldDescription}
							onOpenOperatorPicker={closeAllPopups}
							hideOperatorDropdown={hideOperatorDropdown}
							isIrremovable={isIrremovable}
							contextJql={contextJql}
							viewContext={viewContext}
							scope={scope}
							suggestedUsersCount={suggestedUsersCount}
						/>
					</FieldWrapperNew>
				</ContextualAnalyticsData>
			);
		}
		case PickerTypes.TextArea: {
			return (
				<FieldWrapperNew
					useListElement={Boolean(shouldUseListMarkup)}
					handleKeyDown={handleOnKeyDown}
				>
					<TextAreaPicker
						clause={clause}
						fieldId={fieldId}
						fieldLabel={fieldLabel}
						fieldType={fieldType}
						activePopupKey={activePopupKey}
						onSearchWrapper={onSearchWrapper}
						onClear={onClearCallback}
						onOpen={onOpen}
						onClose={onClose}
						fieldDescription={fieldDescription}
						propType={propType}
						isIrremovable={isIrremovable}
					/>
				</FieldWrapperNew>
			);
		}
		case PickerTypes.DateTime:
		case PickerTypes.DueDate:
		case PickerTypes.DateTimeRange: {
			if (
				(clause && clause.clauseType !== CLAUSE_TYPE_TERMINAL) ||
				(secondaryClause && secondaryClause.clauseType !== CLAUSE_TYPE_TERMINAL)
			) {
				throw new Error(`Picker type ${pickerType} cannot be used with a non terminal clause`);
			}

			return (
				<FieldWrapperNew
					useListElement={Boolean(shouldUseListMarkup)}
					handleKeyDown={handleOnKeyDown}
				>
					<DatePickerWrapper
						clause={clause}
						fieldId={fieldId}
						fieldLabel={fieldLabel}
						pickerType={pickerType}
						activePopupKey={activePopupKey}
						onSearchWrapper={onSearchWrapper}
						onClear={onClearCallback}
						onOpen={onOpen}
						onClose={onClose}
						secondaryClause={secondaryClause}
						fieldDescription={fieldDescription}
						isIrremovable={isIrremovable}
					/>
				</FieldWrapperNew>
			);
		}
		case PickerTypes.CascadeSelect: {
			if (clause && clause.clauseType !== CLAUSE_TYPE_TERMINAL) {
				throw new Error(`Picker type ${pickerType} cannot be used with a non terminal clause`);
			}
			return (
				<FieldWrapperNew
					useListElement={Boolean(shouldUseListMarkup)}
					handleKeyDown={handleOnKeyDown}
				>
					<CascadeSelectPicker
						activePopupKey={activePopupKey}
						clause={clause}
						fieldDescription={fieldDescription}
						fieldId={fieldId}
						fieldLabel={fieldLabel}
						hydratedData={hydratedData}
						onClose={onClose}
						onOpen={onOpen}
						onSearchWrapper={onSearchWrapper}
						onSendOptionAnalytics={sendOptionAnalytics}
						onClear={onClearCallback}
						isIrremovable={isIrremovable}
						contextJql={contextJql}
					/>
				</FieldWrapperNew>
			);
		}
		case PickerTypes.NumberRange: {
			if (
				(clause && clause.clauseType !== CLAUSE_TYPE_TERMINAL) ||
				(secondaryClause && secondaryClause.clauseType !== CLAUSE_TYPE_TERMINAL)
			) {
				throw new Error(`Picker type ${pickerType} cannot be used with a non terminal clause`);
			}
			return (
				<FieldWrapperNew
					useListElement={Boolean(shouldUseListMarkup)}
					handleKeyDown={handleOnKeyDown}
				>
					<NumberRangePicker
						fieldId={fieldId}
						fieldLabel={fieldLabel}
						activePopupKey={activePopupKey}
						clause={clause}
						secondaryClause={secondaryClause}
						onSearchWrapper={onSearchWrapper}
						onClear={onClearCallback}
						onOpen={onOpen}
						onClose={onClose}
						fieldDescription={fieldDescription}
						onSendOptionAnalytics={sendOptionAnalytics}
						isIrremovable={isIrremovable}
					/>
				</FieldWrapperNew>
			);
		}
		case PickerTypes.BooleanType: {
			return (
				<FieldWrapperNew
					useListElement={Boolean(shouldUseListMarkup)}
					handleKeyDown={handleOnKeyDown}
				>
					<LastUserCommentedPickerWrapper
						activePopupKey={activePopupKey}
						clause={clause}
						fieldDescription={fieldDescription}
						fieldId={fieldId}
						fieldLabel={fieldLabel}
						onClose={onClose}
						onOpen={onOpen}
						onSearchWrapper={onSearchWrapper}
						onClear={onClearCallback}
						isIrremovable={isIrremovable}
					/>
				</FieldWrapperNew>
			);
		}
		default:
			return UnsupportedFieldPicker;
	}
};

export default Field;
